import React, { useEffect, Children } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { Header } from './components/Header';
import { VideoPlayer } from './components/VideoPlayer';
import { StatsSection } from './components/StatsSection';
import { HeroHighlight, Highlight } from './components/ui/HeroHighlight';
import { CalendlyEmbed } from './components/CalendlyEmbed';
import { FAQSection } from './components/FAQSection';
import { Footer } from './components/Footer';
import { BookCallButton } from './components/ui/BookCallButton';
import { OnboardingPage } from './pages/OnboardingPage';
import { CulturePage } from './pages/CulturePage';
import { DnsPage } from './pages/DnsPage';
import { IndeedPage } from './pages/IndeedPage';
import { CompletionPage } from './pages/CompletionPage';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
function HomePage() {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const scrollToCalendly = () => {
    const element = document.getElementById('calendly');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  // Animation variants
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100,
        duration: 0.4
      }
    }
  };
  return <div className="w-full min-h-screen" data-id="element-0">
      <HeroHighlight data-id="element-1">
        <Header data-id="element-2" />
        <motion.main className="max-w-6xl mx-auto px-4" initial="hidden" animate="visible" variants={containerVariants} data-id="element-3">
          <motion.div id="headline" variants={itemVariants} data-id="element-4">
            <div className="flex justify-center mb-4 sm:mb-6 items-center" data-id="element-5">
              {/* Only show forward arrow on homepage */}
              <div className="flex items-center" data-id="element-6">
                <div className="inline-flex items-center px-3 sm:px-6 py-1.5 sm:py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm" data-id="element-7">
                  <span className="text-xs sm:text-sm md:text-base font-medium text-white" data-id="element-8">
                    STEP
                  </span>
                  <div className="inline-flex items-center justify-center w-5 h-5 sm:w-7 sm:h-7 mx-1 sm:mx-2 rounded-full bg-gradient-to-r from-blue-400 to-indigo-600" data-id="element-9">
                    <span className="text-xs sm:text-base font-bold text-white" data-id="element-10">
                      1
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm md:text-base font-medium text-white" data-id="element-11">
                    OF 5
                  </span>
                </div>
                <motion.button onClick={() => navigate('/onboarding')} className="ml-2 sm:ml-3 h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-white hover:bg-white/10 transition-colors" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} data-id="element-12">
                  <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-13" />
                </motion.button>
              </div>
            </div>
            <h1 className="text-center text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 md:mb-8 max-w-4xl mx-auto leading-[1.4] sm:leading-[1.3] text-white" data-id="element-14">
              Welcome to <Highlight data-id="element-15">HIRE!</Highlight> Please dedicate time to
              watching the full video below
            </h1>
            <div className="mb-10 sm:mb-12" data-id="element-16">
              <p className="text-center text-base sm:text-xl mb-4 sm:mb-6 text-white" data-id="element-17">
                <strong data-id="element-18">
                  Tap play on the video below (Watch at 1.5x speed for faster
                  efficiency)
                </strong>
              </p>
              <VideoPlayer data-id="element-19" />
            </div>
          </motion.div>
          <motion.div id="calendly" className="text-center mb-6" variants={itemVariants} data-id="element-20">
            <div className="flex justify-center mb-8" data-id="element-21">
              <div className="w-full max-w-md" data-id="element-22">
                <BookCallButton to="/onboarding" className="py-4 px-10 text-xl sm:text-2xl" showConfirmation={false} // No confirmation on first page
              data-id="element-23" />
              </div>
            </div>
          </motion.div>
          <motion.div variants={itemVariants} data-id="element-24">
            <CalendlyEmbed data-id="element-25" />
          </motion.div>
          <motion.div id="stats" variants={itemVariants} data-id="element-26">
            <StatsSection data-id="element-27" />
          </motion.div>
          <motion.div id="faq" variants={itemVariants} data-id="element-28">
            <FAQSection data-id="element-29" />
          </motion.div>
          <Footer data-id="element-30" />
        </motion.main>
      </HeroHighlight>
    </div>;
}
export function App() {
  return <BrowserRouter data-id="element-31">
      <Routes data-id="element-32">
        <Route path="/" element={<HomePage data-id="element-34" />} data-id="element-33" />
        <Route path="/onboarding" element={<OnboardingPage data-id="element-36" />} data-id="element-35" />
        <Route path="/culture" element={<CulturePage data-id="element-38" />} data-id="element-37" />
        <Route path="/dns" element={<DnsPage data-id="element-40" />} data-id="element-39" />
        <Route path="/indeed" element={<IndeedPage data-id="element-42" />} data-id="element-41" />
        <Route path="/completion" element={<CompletionPage data-id="element-44" />} data-id="element-43" />
      </Routes>
    </BrowserRouter>;
}